import { pdf } from '@react-pdf/renderer';
import { useGetPatientInfo } from 'features/patient/_utils/patient-info/hook-context-patientinfo';
import {
  ConcussionAnalysis,
  PatientInfoState
} from 'features/patient/_utils/patient-info/patient-info-slice-types';
import { CgsInventory } from 'features/patient/assessment/questionnaires/cgs/cgsSlice';
import {
  useGetCGSInfo,
  useGetConcussions
} from 'lib/hooks/tanstack/use-getInventoryInfo';
import { downloadBlob } from 'lib/utils/print/print-actions';
import { useCallback, useState } from 'react';
import { flushSync, render } from 'react-dom';
import {
  SerialiseBrainFunctionVisualisations,
  SerialiseConcussionComplexityScale
} from '../components/serialise-svg-component';
import { PatientDashboardReport } from '../pdf-report';
import { createImageStore, UseImageStoreReturn } from './create-image-store';
import { saveGraphs, saveSVGs } from './saveSVGs';

/**
 * TEMPORARY: A component that generates a Test-Taker report on the client-side
 * TODO: Migrate the logic to the backend
 *
 * Phases
 * 1. Export visualisations as images.
 * 1.1 Components that use visualisations as SVGs are rendered in a temporary React Virtual DOM and the SVGs are extracted.
 * 1.2 Plotly components are rendered to images directly.
 * 2. Insert the content into the PDF through an image store
 */
export function usePrintReportPipeline(params: { patientId: string }) {
  const [progress, setProgress] = useState({
    progress: 0
  });

  const patientInfo = useGetPatientInfo(params.patientId);
  const cgsQuery = useGetCGSInfo(params.patientId);
  const concussionQuery = useGetConcussions(params.patientId);

  const handlePrint = useCallback(async () => {
    await onClick(
      patientInfo.state,
      cgsQuery.data ?? [],
      concussionQuery.data ?? [],
      progress => setProgress(_ => ({ ..._, ...progress }))
    );
  }, [patientInfo.state, cgsQuery.data, concussionQuery.data]);

  return {
    handlePrint,
    progress
  };
}

/**
 * Use a temporary React virtual DOM to render react components and serialise the generated HTML
 * @param imageStore
 * @param patientInfo
 * @returns
 */
async function serialiseReactVisualisationComponents(
  imageStore: UseImageStoreReturn,
  patientInfo: PatientInfoState
) {
  const temporaryRoot = document.createElement('div');

  /**
   * We use `flushSync` to force any memoised callbacks to trigger
   *
   * https://react.dev/reference/react-dom/flushSync
   */
  flushSync(() => {
    render(
      <div>
        <SerialiseBrainFunctionVisualisations
          patientInfo={patientInfo}
          registerImage={imageStore.setImage}
        />
        <SerialiseConcussionComplexityScale
          patientInfo={patientInfo}
          registerImage={imageStore.setImage}
        />
      </div>,
      temporaryRoot
    );
  });

  /**
   * Create a copy of the rendered component. React will invalidate the old reference when a state update is made in the React rendering pipeline
   */
  const rootComponent = document.createElement('div');
  rootComponent.innerHTML = temporaryRoot.innerHTML;
  return rootComponent;
}

async function onClick(
  patientInfo: PatientInfoState,
  cgsData: CgsInventory[],
  concussionData: ConcussionAnalysis[],
  onProgress: (params: { progress: number; description?: string }) => void
) {
  const imageStore = createImageStore();
  const rootComponent = await serialiseReactVisualisationComponents(
    imageStore,
    patientInfo
  );
  onProgress({ progress: 1 });

  await saveSVGs(imageStore, rootComponent);
  await saveGraphs(imageStore, cgsData, concussionData);

  const pdfOutput = pdf(
    <PatientDashboardReport patientInfo={patientInfo} imageStore={imageStore} />
  );

  const blob = await pdfOutput.toBlob();
  downloadBlob('output-1.pdf', blob);

  onProgress({ progress: 100 });
}
